import permissionDir, {
  permissionShow,
  permissionDisabled
} from '@/directives/permission';
import { permissionDirective, isCompanyUserDirective } from '@/directives/auth';
import i18n from '@/locales/i18n';
import { setupCustomComponents } from '@/plugins/customComponents';
import uxd, { message } from '@koi-design/uxd-ui';
import 'core-js/stable/structured-clone';
import 'core-js/stable/array/at';
import 'virtual:svg-icons-register';
import { request } from '@koi-design/vix-tools';
import * as Sentry from '@sentry/vue';
import { createApp } from 'vue';
import packageJson from '../package.json';
import App from './App.vue';
import { requestConfig } from './config';
import router from './router';
import dompurifyPlugin from './plugins/dompurify';
import { faviconSrc } from './libs/img';
import store from './store';
import isBaiyuHardwareEnabled, {
  initBaiyuHardwareEmbedContext,
  setupBaiyuHardwareEmbedMessageListener
} from '@libs/isBaiyuHardwareEnabled';
import { setupIframeRouteSync } from '@libs/iframeRouteSync';
import { setAuthRouter } from '@libs/authSession';
import './styles/color-palette.css';
import './styles/lastVersion.css';

initBaiyuHardwareEmbedContext();
setupBaiyuHardwareEmbedMessageListener();
setAuthRouter(router);
setupIframeRouteSync(router);

const setThemeSource = async () => {
  if (IS_DOMESTIC) {
    document.documentElement.setAttribute('data-theme', 'purple');
    await import('./styles/theme-purple.css');
  } else {
    document.documentElement.setAttribute('data-theme', 'blue');
    await import('./styles/theme-blue.css');
  }
};
if (!IS_DOMESTIC) {
  localStorage.setItem('skin', 'blue');
  // Seed the overseas default on first visit only: overwriting it here
  // would discard the language the user picked (the switch reloads).
  if (!localStorage.getItem('language')) {
    localStorage.setItem('language', 'en_gaia');
  }
}
setThemeSource();
request.init(requestConfig);
message.config({
  top: '80px',
  maxCount: 1
});

// Get default title based on locale
const getDefaultTitle = () => {
  if (isBaiyuHardwareEnabled()) {
    return i18n.global.t('main.f542dd00f54aafad7351e2e24a9c0abb');
  }
  if (IS_DOMESTIC) {
    return i18n.global.t('main.e0b464a446e016c314adc37d337b02f0');
  }
  return 'Agent Studio';
};

// Set document head (title + meta)
const setDocumentHead = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const docTitle = urlParams.get('docTitle');
  const key = urlParams.get('key');
  const id = urlParams.get('id');
  const token = urlParams.get('token');
  const share = key && id & token;
  if (docTitle) {
    document.title = docTitle;
  } else {
    document.title = getDefaultTitle();
  }

  // Baiyu SEO meta tags
  if (isBaiyuHardwareEnabled()) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', i18n.global.t('main.6eab7f560a810ee60d7a92c7168aedc6'));

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', i18n.global.t('views.agentShop.index.793cf37eca2dc71661bf4ed4769f8060'));
  }
};

setDocumentHead();

const link = document.createElement('link');
link.rel = 'icon';
console.log(faviconSrc);
link.href = new URL(faviconSrc, import.meta.url).href;
document.head.appendChild(link);

const app = createApp(App);
const shouldEnableSentry = !window.location.hostname.toLowerCase().includes('pre') && !import.meta.env.DEV;

const sentryRelease = `saibotan-frontend@${packageJson.version}`;

if (shouldEnableSentry) {
  Sentry.init({
    app,
    dsn: 'https://4c5dce0b41391941bd45417356b66c0b@trace.100credit.com/13',
    environment: import.meta.env.MODE || 'development',
    release: sentryRelease,
    tracesSampleRate: 1.0,
    ignoreErrors: [/^Failed to fetch/],
    integrations: [
      Sentry.browserTracingIntegration({
        router
      })
    ]
  });
}

app.config.devtools = true;
app.use(uxd);
app.use(i18n);
app.use(dompurifyPlugin);
app.directive('auth', permissionDirective);
app.directive('permission', permissionDir);
app.directive('permission-show', permissionShow);
app.directive('permission-disabled', permissionDisabled);
app.directive('company', isCompanyUserDirective);
setupCustomComponents(app);
app.config.globalProperties.IS_DOMESTIC = IS_DOMESTIC;
app.use(store).use(router);

app.mount('#app');
